/**
 * PlantasIotStripHistoryModal.jsx  v1
 * Historial del strip (base plantas_iot) — mismo formato que StripHistoryModal de Alvear
 * Tipos: nivel · propano · butano · total
 */

import { useState, useEffect } from 'react';
import {
    Box, CircularProgress, Dialog, DialogContent, DialogTitle,
    IconButton, Paper, Stack, Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { LineChart } from '@mui/x-charts/LineChart';
import dayjs from 'dayjs';
import RangoFechas, { mesActual } from '../alvear/Rangofechas';

const TITULOS = {
    nivel: 'Nivel por tanque — histórico',
    propano: 'Propano (tn) — histórico',
    butano: 'Butano (tn) — histórico',
    total: 'Total disponible (tn) — histórico',
};

// Un color por tanque (no por producto, para distinguir tanques del mismo producto)
const COLORES_TANQUE = ['#38BDF8', '#FB923C', '#A78BFA', '#22C55E', '#F472B6', '#FACC15', '#2DD4BF', '#F87171'];

export const COLOR_PRODUCTO = { propano: '#38BDF8', butano: '#FB923C' };

const redondear = (v) => Number(v.toFixed(2));

export default function PlantasIotStripHistoryModal({ open, tipo, tanques, plantaId, apiUrl, onClose }) {
    const [rango, setRango] = useState(() => mesActual());
    const [filas, setFilas] = useState([]);
    const [loading, setLoading] = useState(false);

    const headers = { 'x-api-key': process.env.REACT_APP_HMI_API_KEY || '' };

    useEffect(() => {
        if (open) setRango(mesActual());
    }, [open]);

    useEffect(() => {
        if (!open || !tipo || !plantaId) return;
        let activo = true;
        setLoading(true);
        fetch(`${apiUrl}/api/plantas-iot/${plantaId}/history?from=${encodeURIComponent(rango.from)}&to=${encodeURIComponent(rango.to)}&puntos=300`, { headers })
            .then((r) => r.json())
            .then((d) => {
                if (!activo) return;
                // Pivotear: una fila por momento, una columna por tanque
                const porTs = new Map();
                (d.puntos || []).forEach((p) => {
                    if (!porTs.has(p.ts)) porTs.set(p.ts, { ts: p.ts, fecha: dayjs(p.ts).format('DD/MM HH:mm') });
                    const fila = porTs.get(p.ts);
                    fila[`tk${p.tanque_id}_pct`] = p.volumen_pct;
                    fila[`tk${p.tanque_id}_tn`] = p.masa_kg !== null ? redondear(p.masa_kg / 1000) : null;
                });
                const ordenadas = [...porTs.values()].sort((a, b) => (a.ts < b.ts ? -1 : 1));
                setFilas(ordenadas);
            })
            .catch(() => activo && setFilas([]))
            .finally(() => activo && setLoading(false));
        return () => { activo = false; };
    }, [open, tipo, plantaId, rango, apiUrl]);

    if (!tipo) return null;

    // Tanques con su color fijo según el orden de la planta
    const lista = (tanques || []).map((t, i) => ({
        ...t,
        prod: (t.producto || '').toLowerCase(),
        color: COLORES_TANQUE[i % COLORES_TANQUE.length],
    }));

    // Suma de toneladas de un grupo de tanques; null si falta alguno en ese momento
    const sumar = (fila, grupo) => {
        if (grupo.length === 0) return null;
        let total = 0;
        for (const t of grupo) {
            const v = fila[`tk${t.tanque_id}_tn`];
            if (v === null || v === undefined) return null;
            total += v;
        }
        return redondear(total);
    };

    const serie = (dataKey, label, color) => ({
        dataKey, label, color, showMark: false, curve: 'monotoneX', connectNulls: true,
    });

    const buildChart = () => {
        if (tipo === 'nivel') {
            return {
                series: lista.map((t) => serie(`tk${t.tanque_id}_pct`, t.tanque, t.color)),
                dataset: filas,
                yAxis: { min: 0, max: 100, label: '%' },
            };
        }

        if (tipo === 'propano' || tipo === 'butano') {
            const grupo = lista.filter((t) => t.prod === tipo);
            return {
                series: grupo.map((t) => serie(`tk${t.tanque_id}_tn`, t.tanque, t.color)),
                dataset: filas,
                yAxis: { label: 'tn' },
            };
        }

        if (tipo === 'total') {
            const propano = lista.filter((t) => t.prod === 'propano');
            const butano = lista.filter((t) => t.prod === 'butano');
            const dataset = filas.map((f) => ({
                ...f,
                propano_total: sumar(f, propano),
                butano_total: sumar(f, butano),
                total_tn: sumar(f, lista),
            }));
            const series = [];
            if (propano.length) series.push(serie('propano_total', 'Propano', COLOR_PRODUCTO.propano));
            if (butano.length) series.push(serie('butano_total', 'Butano', COLOR_PRODUCTO.butano));
            series.push(serie('total_tn', 'Total', '#22C55E'));
            return { series, dataset, yAxis: { label: 'tn' } };
        }

        return { series: [], dataset: [], yAxis: {} };
    };

    const { series, dataset, yAxis } = buildChart();
    const tickCada = Math.max(1, Math.ceil(dataset.length / 8));

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
            PaperProps={{ sx: { borderRadius: 2, maxHeight: '92vh' } }}>

            <DialogTitle sx={{ pb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
                    <Typography variant="subtitle1" fontWeight={700}>
                        {TITULOS[tipo]}
                    </Typography>
                    <IconButton size="small" onClick={onClose} sx={{ flexShrink: 0 }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Stack>

                <Box sx={{ mt: 1.5 }}>
                    <RangoFechas value={rango} onChange={setRango} />
                </Box>
            </DialogTitle>

            <DialogContent sx={{ pt: 2 }}>
                {loading ? (
                    <Stack alignItems="center" py={4}>
                        <CircularProgress size={28} />
                        <Typography variant="caption" color="text.disabled" mt={1}>Cargando histórico...</Typography>
                    </Stack>
                ) : dataset.length === 0 || series.length === 0 ? (
                    <Stack alignItems="center" py={4}>
                        <Typography variant="body2" color="text.disabled">Sin datos en el período seleccionado</Typography>
                    </Stack>
                ) : (
                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                        {/* Leyenda */}
                        <Stack direction="row" spacing={2} mb={1} flexWrap="wrap">
                            {series.map((s) => (
                                <Stack key={s.dataKey} direction="row" alignItems="center" spacing={0.5}>
                                    <Box sx={{ width: 12, height: 2, bgcolor: s.color, borderRadius: 1 }} />
                                    <Typography variant="caption" color="text.disabled" fontSize={10}>{s.label}</Typography>
                                </Stack>
                            ))}
                        </Stack>
                        <LineChart
                            dataset={dataset}
                            series={series}
                            xAxis={[{
                                dataKey: 'fecha', scaleType: 'band',
                                tickLabelStyle: { fontSize: 8 },
                                tickInterval: (_, i) => i % tickCada === 0,
                            }]}
                            yAxis={[{ tickLabelStyle: { fontSize: 9 }, ...yAxis }]}
                            height={320}
                            margin={{ left: 50, right: 16, top: 8, bottom: 32 }}
                            slotProps={{ legend: { hidden: true } }}
                        />
                    </Paper>
                )}
            </DialogContent>
        </Dialog>
    );
}